import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useFamily } from '../contexts/FamilyContext';
import ImageSelector from '../components/ImageSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUtensils, faPalette, faBookOpen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { MobileInput, MobileTextarea, MobileButton } from '../components/mobile/MobileFormComponents';
import { PageHeader } from '../design-system';

const DIFFICULTY_OPTIONS = ['Easy', 'Medium', 'Hard'];
const AGE_RANGE_OPTIONS = ['0-2', '3-5', '5-8', '8-12', '12+', 'All Ages'];
const ACTIVITY_CATEGORY_OPTIONS = ['Games', 'Crafts', 'Music', 'Cooking', 'Reading', 'Outdoor', 'Indoor', 'Educational', 'Creative'];
const STORY_CATEGORY_OPTIONS = ['Holiday', 'Traditional', 'Modern', 'Educational', 'Inspirational', 'Historical', 'Fictional'];
const TAG_OPTIONS = {
  recipe: ['Shabbat', 'Passover', 'Rosh Hashanah', 'Yom Kippur', 'Sukkot', 'Hanukkah', 'Purim', 'Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Kosher', 'Traditional', 'Modern', 'Kids-Friendly'],
  activity: ['Shabbat', 'Passover', 'Rosh Hashanah', 'Yom Kippur', 'Sukkot', 'Hanukkah', 'Purim', 'Kids-Friendly', 'Family Activity', 'Traditional', 'Modern', 'Educational', 'Fun', 'Interactive'],
  story: ['Shabbat', 'Passover', 'Rosh Hashanah', 'Yom Kippur', 'Sukkot', 'Hanukkah', 'Purim', 'Kids-Friendly', 'Family Story', 'Traditional', 'Modern', 'Educational', 'Bedtime Story']
};

export default function SubmitContent() {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || 'recipe';
  const [contentType, setContentType] = useState(initialType); // 'recipe' | 'activity' | 'story'
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { familyId } = useFamily();

  // Recipe form data
  const [recipeData, setRecipeData] = useState({
    title: '',
    description: '',
    prepTime: '',
    cookTime: '',
    serves: '',
    difficulty: 'Medium',
    ingredients: [{ name: '', amount: '' }],
    instructions: [''],
    culturalContext: '',
    tags: [],
    attribution: ''
  });

  // Activity form data
  const [activityData, setActivityData] = useState({
    title: '',
    description: '',
    ageRange: '',
    time: '',
    category: '',
    materials: [''],
    instructions: [''],
    culturalContext: '',
    tags: [],
    attribution: ''
  });

  // Story form data
  const [storyData, setStoryData] = useState({
    title: '',
    description: '',
    ageRange: '',
    category: '',
    content: '',
    readAloud: false,
    culturalContext: '',
    tags: [],
    attribution: ''
  });

  const [imageData, setImageData] = useState(null);
  const [errors, setErrors] = useState({});

  // Reset form when content type changes
  useEffect(() => {
    setErrors({});
  }, [contentType]);

  const handleImageSelect = (data) => {
    setImageData(data);
  };

  const getDefaultSearchQuery = () => {
    const currentData = contentType === 'recipe' ? recipeData : contentType === 'activity' ? activityData : storyData;
    if (currentData.title) return currentData.title;
    return '';
  };

  // Recipe handlers
  const handleRecipeInputChange = (field, value) => {
    setRecipeData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleAddIngredient = () => {
    setRecipeData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: '', amount: '' }]
    }));
  };

  const handleRemoveIngredient = (index) => {
    setRecipeData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const handleIngredientChange = (index, field, value) => {
    setRecipeData(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => 
        i === index ? { ...ing, [field]: value } : ing
      )
    }));
  };

  const handleAddInstruction = (type) => {
    if (type === 'recipe') {
      setRecipeData(prev => ({
        ...prev,
        instructions: [...prev.instructions, '']
      }));
    } else {
      setActivityData(prev => ({
        ...prev,
        instructions: [...prev.instructions, '']
      }));
    }
  };

  const handleRemoveInstruction = (index, type) => {
    if (type === 'recipe') {
      setRecipeData(prev => ({
        ...prev,
        instructions: prev.instructions.filter((_, i) => i !== index)
      }));
    } else {
      setActivityData(prev => ({
        ...prev,
        instructions: prev.instructions.filter((_, i) => i !== index)
      }));
    }
  };

  const handleInstructionChange = (index, value, type) => {
    if (type === 'recipe') {
      setRecipeData(prev => ({
        ...prev,
        instructions: prev.instructions.map((inst, i) => i === index ? value : inst)
      }));
    } else {
      setActivityData(prev => ({
        ...prev,
        instructions: prev.instructions.map((inst, i) => i === index ? value : inst)
      }));
    }
  };

  // Activity handlers
  const handleActivityInputChange = (field, value) => {
    setActivityData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleAddMaterial = () => {
    setActivityData(prev => ({
      ...prev,
      materials: [...prev.materials, '']
    }));
  };

  const handleRemoveMaterial = (index) => {
    setActivityData(prev => ({
      ...prev,
      materials: prev.materials.filter((_, i) => i !== index)
    }));
  };

  const handleMaterialChange = (index, value) => {
    setActivityData(prev => ({
      ...prev,
      materials: prev.materials.map((mat, i) => i === index ? value : mat)
    }));
  };

  // Story handlers
  const handleStoryInputChange = (field, value) => {
    setStoryData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  // Tag handlers
  const handleTagToggle = (tag) => {
    if (contentType === 'recipe') {
      setRecipeData(prev => ({
        ...prev,
        tags: prev.tags.includes(tag)
          ? prev.tags.filter(t => t !== tag)
          : [...prev.tags, tag]
      }));
    } else if (contentType === 'activity') {
      setActivityData(prev => ({
        ...prev,
        tags: prev.tags.includes(tag)
          ? prev.tags.filter(t => t !== tag)
          : [...prev.tags, tag]
      }));
    } else {
      setStoryData(prev => ({
        ...prev,
        tags: prev.tags.includes(tag)
          ? prev.tags.filter(t => t !== tag)
          : [...prev.tags, tag]
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (contentType === 'recipe') {
      if (!recipeData.title.trim()) newErrors.title = 'Title is required';
      if (!recipeData.description.trim()) newErrors.description = 'Description is required';
      else if (recipeData.description.length > 500) newErrors.description = 'Description must be 500 characters or less';
      if (!recipeData.prepTime.trim()) newErrors.prepTime = 'Prep time is required';
      if (!recipeData.cookTime.trim()) newErrors.cookTime = 'Cook time is required';
      if (!recipeData.serves.trim()) newErrors.serves = 'Serves is required';
      const validIngredients = recipeData.ingredients.filter(ing => ing.name.trim() && ing.amount.trim());
      if (validIngredients.length === 0) newErrors.ingredients = 'At least one ingredient is required';
      const validInstructions = recipeData.instructions.filter(inst => inst.trim());
      if (validInstructions.length === 0) newErrors.instructions = 'At least one instruction is required';
      if (recipeData.culturalContext.length > 1000) newErrors.culturalContext = 'Cultural context must be 1000 characters or less';
    } else if (contentType === 'activity') {
      if (!activityData.title.trim()) newErrors.title = 'Title is required';
      if (!activityData.description.trim()) newErrors.description = 'Description is required';
      else if (activityData.description.length > 500) newErrors.description = 'Description must be 500 characters or less';
      if (!activityData.ageRange) newErrors.ageRange = 'Age range is required';
      if (!activityData.time.trim()) newErrors.time = 'Duration/time is required';
      const validMaterials = activityData.materials.filter(mat => mat.trim());
      if (validMaterials.length === 0) newErrors.materials = 'At least one material is required';
      const validInstructions = activityData.instructions.filter(inst => inst.trim());
      if (validInstructions.length === 0) newErrors.instructions = 'At least one instruction is required';
      if (activityData.culturalContext.length > 1000) newErrors.culturalContext = 'Cultural context must be 1000 characters or less';
    } else {
      if (!storyData.title.trim()) newErrors.title = 'Title is required';
      if (!storyData.description.trim()) newErrors.description = 'Description is required';
      else if (storyData.description.length > 500) newErrors.description = 'Description must be 500 characters or less';
      if (!storyData.ageRange) newErrors.ageRange = 'Age range is required';
      if (!storyData.content.trim()) newErrors.content = 'Story content is required';
      else if (storyData.content.length < 100) newErrors.content = 'Story content must be at least 100 characters';
      else if (storyData.content.length > 5000) newErrors.content = 'Story content must be 5000 characters or less';
      if (storyData.culturalContext.length > 1000) newErrors.culturalContext = 'Cultural context must be 1000 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user || !familyId) {
      toast.error('You must be logged in to submit content');
      return;
    }

    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      let submissionData = {
        type: contentType,
        status: 'pending',
        submittedBy: user.uid,
        submitterName: user.displayName || user.email?.split('@')[0] || 'Anonymous',
        familyId: familyId,
        submittedAt: Timestamp.now(),
        reviewedAt: null,
        reviewedBy: null,
        rejectionReason: null,
        claps: 0,
        clappedBy: [],
        commentsCount: 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // Prepare content based on type
      if (contentType === 'recipe') {
        submissionData.content = {
          title: recipeData.title.trim(),
          description: recipeData.description.trim(),
          prepTime: recipeData.prepTime.trim(),
          cookTime: recipeData.cookTime.trim(),
          serves: recipeData.serves.trim(),
          difficulty: recipeData.difficulty,
          ingredients: recipeData.ingredients
            .filter(ing => ing.name.trim() && ing.amount.trim())
            .map(ing => ({
              name: ing.name.trim(),
              amount: ing.amount.trim()
            })),
          instructions: recipeData.instructions
            .filter(inst => inst.trim())
            .map(inst => inst.trim()),
          culturalContext: recipeData.culturalContext.trim() || null,
          tags: recipeData.tags,
          attribution: recipeData.attribution.trim() || null
        };
      } else if (contentType === 'activity') {
        submissionData.content = {
          title: activityData.title.trim(),
          description: activityData.description.trim(),
          ageRange: activityData.ageRange,
          time: activityData.time.trim(),
          category: activityData.category.trim() || null,
          materials: activityData.materials
            .filter(mat => mat.trim())
            .map(mat => mat.trim()),
          instructions: activityData.instructions
            .filter(inst => inst.trim())
            .map(inst => inst.trim()),
          culturalContext: activityData.culturalContext.trim() || null,
          tags: activityData.tags,
          attribution: activityData.attribution.trim() || null
        };
      } else {
        submissionData.content = {
          title: storyData.title.trim(),
          description: storyData.description.trim(),
          ageRange: storyData.ageRange,
          category: storyData.category.trim() || null,
          content: storyData.content.trim(),
          readAloud: storyData.readAloud,
          culturalContext: storyData.culturalContext.trim() || null,
          tags: storyData.tags,
          attribution: storyData.attribution.trim() || null
        };
      }

      // Handle image
      if (imageData) {
        if (imageData.type === 'unsplash') {
          submissionData.content.unsplashPhoto = imageData.unsplashPhoto;
        } else if (imageData.type === 'upload') {
          submissionData.content.imageUrl = imageData.uploadUrl;
          submissionData.content.imageCaption = imageData.caption || null;
        }
      }

      await addDoc(collection(db, 'submissions'), submissionData);

      toast.success(`Thanks! Your ${contentType} submission is under review.`);
      navigate('/explore');
    } catch (error) {
      console.error('Error submitting content:', error);
      toast.error('Failed to submit content. Please try again.');
      setSubmitting(false);
    }
  };

  const currentTags = contentType === 'recipe' ? recipeData.tags : contentType === 'activity' ? activityData.tags : storyData.tags;
  const currentData = contentType === 'recipe' ? recipeData : contentType === 'activity' ? activityData : storyData;

  return (
    <div className="min-h-screen bg-background pt-14 pb-20 md:pt-0 md:pb-0">
      <PageHeader
        title="Submit Content"
        subtitle="Share a recipe, activity, or story with the community"
        maxWidth="896px"
      />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 md:py-8">

        {/* Content Type Selector */}
        <div className="mb-6 md:mb-8">
          <label className="block text-sm font-semibold text-text-primary mb-3">
            What would you like to share?
          </label>
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            {[
              { type: 'recipe', icon: faUtensils, label: 'Recipe' },
              { type: 'activity', icon: faPalette, label: 'Activity' },
              { type: 'story', icon: faBookOpen, label: 'Story' }
            ].map(({ type, icon, label }) => (
              <motion.button
                key={type}
                type="button"
                whileTap={{ scale: 0.98 }}
                onClick={() => setContentType(type)}
                className={`
                  p-4 md:p-6 rounded-2xl border-2 transition-all touch-target
                  ${contentType === type
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border hover:border-primary/50 active:bg-gray-50'
                  }
                `}
              >
                <FontAwesomeIcon icon={icon} className="text-2xl md:text-4xl mb-2 text-primary" />
                <div className="font-semibold text-sm md:text-base text-text-primary">{label}</div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-border p-4 md:p-8 space-y-4 md:space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={currentData.title}
              onChange={(e) => {
                if (contentType === 'recipe') handleRecipeInputChange('title', e.target.value);
                else if (contentType === 'activity') handleActivityInputChange('title', e.target.value);
                else handleStoryInputChange('title', e.target.value);
              }}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-primary text-base ${
                errors.title ? 'border-red-500' : 'border-border'
              }`}
              placeholder={`Give your ${contentType} a title`}
              required
            />
            {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Description <span className="text-red-500">*</span>
              <span className="text-xs text-text-secondary font-normal ml-2">
                ({currentData.description.length}/500)
              </span>
            </label>
            <textarea
              value={currentData.description}
              onChange={(e) => {
                if (contentType === 'recipe') handleRecipeInputChange('description', e.target.value);
                else if (contentType === 'activity') handleActivityInputChange('description', e.target.value);
                else handleStoryInputChange('description', e.target.value);
              }}
              rows={3}
              maxLength={500}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-primary resize-none text-base ${
                errors.description ? 'border-red-500' : 'border-border'
              }`}
              placeholder={`Brief description of the ${contentType}...`}
              required
            />
            {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
          </div>

          {/* Recipe-specific fields */}
          {contentType === 'recipe' && (
            <>
              {/* Prep Time, Cook Time, Serves */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Prep Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={recipeData.prepTime}
                    onChange={(e) => handleRecipeInputChange('prepTime', e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-primary text-base ${
                      errors.prepTime ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="e.g., 30 minutes"
                    required
                  />
                  {errors.prepTime && <p className="mt-1 text-sm text-red-500">{errors.prepTime}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Cook Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={recipeData.cookTime}
                    onChange={(e) => handleRecipeInputChange('cookTime', e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-primary text-base ${
                      errors.cookTime ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="e.g., 2 hours"
                    required
                  />
                  {errors.cookTime && <p className="mt-1 text-sm text-red-500">{errors.cookTime}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Serves <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={recipeData.serves}
                    onChange={(e) => handleRecipeInputChange('serves', e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-primary text-base ${
                      errors.serves ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="e.g., 8-10"
                    required
                  />
                  {errors.serves && <p className="mt-1 text-sm text-red-500">{errors.serves}</p>}
                </div>
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Difficulty <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-3 flex-wrap">
                  {DIFFICULTY_OPTIONS.map((difficulty) => (
                    <button
                      key={difficulty}
                      type="button"
                      onClick={() => handleRecipeInputChange('difficulty', difficulty)}
                      className={`px-4 py-2 rounded-lg border-2 transition-colors touch-target ${
                        recipeData.difficulty === difficulty
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-text-primary border-border hover:border-primary'
                      }`}
                    >
                      {difficulty}
                    </button>
                  ))}
                </div>
              </div>

              {/* Ingredients */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Ingredients <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {recipeData.ingredients.map((ingredient, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={ingredient.name}
                        onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                        className="flex-1 px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary text-base"
                        placeholder="Ingredient name"
                      />
                      <input
                        type="text"
                        value={ingredient.amount}
                        onChange={(e) => handleIngredientChange(index, 'amount', e.target.value)}
                        className="w-32 px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary text-base"
                        placeholder="Amount"
                      />
                      {recipeData.ingredients.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveIngredient(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors touch-target"
                          aria-label="Remove ingredient"
                        >
                          <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddIngredient}
                    className="flex items-center gap-2 px-4 py-2 text-primary hover:bg-primary/10 rounded-lg transition-colors touch-target"
                  >
                    <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                    Add Ingredient
                  </button>
                </div>
                {errors.ingredients && <p className="mt-1 text-sm text-red-500">{errors.ingredients}</p>}
              </div>

              {/* Instructions */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Instructions <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {recipeData.instructions.map((instruction, index) => (
                    <div key={index} className="flex gap-2">
                      <span className="flex-shrink-0 w-8 h-10 flex items-center justify-center text-text-secondary font-semibold">
                        {index + 1}.
                      </span>
                      <textarea
                        value={instruction}
                        onChange={(e) => handleInstructionChange(index, e.target.value, 'recipe')}
                        rows={2}
                        className="flex-1 px-4 py-2 border-2 border-border rounded-xl focus:outline-none focus:border-primary resize-none text-base"
                        placeholder="Instruction step..."
                      />
                      {recipeData.instructions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveInstruction(index, 'recipe')}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors touch-target"
                          aria-label="Remove instruction"
                        >
                          <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => handleAddInstruction('recipe')}
                    className="flex items-center gap-2 px-4 py-2 text-primary hover:bg-primary/10 rounded-lg transition-colors touch-target"
                  >
                    <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                    Add Step
                  </button>
                </div>
                {errors.instructions && <p className="mt-1 text-sm text-red-500">{errors.instructions}</p>}
              </div>
            </>
          )}

          {/* Activity-specific fields */}
          {contentType === 'activity' && (
            <>
              {/* Age Range and Time */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Age Range <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={activityData.ageRange}
                    onChange={(e) => handleActivityInputChange('ageRange', e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-primary text-base ${
                      errors.ageRange ? 'border-red-500' : 'border-border'
                    }`}
                    required
                    style={{ fontSize: '16px' }}
                  >
                    <option value="">Select age range</option>
                    {AGE_RANGE_OPTIONS.map(age => (
                      <option key={age} value={age}>{age}</option>
                    ))}
                  </select>
                  {errors.ageRange && <p className="mt-1 text-sm text-red-500">{errors.ageRange}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Duration/Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={activityData.time}
                    onChange={(e) => handleActivityInputChange('time', e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-primary text-base ${
                      errors.time ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="e.g., 30 minutes"
                    required
                  />
                  {errors.time && <p className="mt-1 text-sm text-red-500">{errors.time}</p>}
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Category
                </label>
                <select
                  value={activityData.category}
                  onChange={(e) => handleActivityInputChange('category', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary text-base"
                  style={{ fontSize: '16px' }}
                >
                  <option value="">Select category (optional)</option>
                  {ACTIVITY_CATEGORY_OPTIONS.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Materials */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Materials <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {activityData.materials.map((material, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={material}
                        onChange={(e) => handleMaterialChange(index, e.target.value)}
                        className="flex-1 px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary text-base"
                        placeholder="Material needed"
                      />
                      {activityData.materials.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveMaterial(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors touch-target"
                          aria-label="Remove material"
                        >
                          <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddMaterial}
                    className="flex items-center gap-2 px-4 py-2 text-primary hover:bg-primary/10 rounded-lg transition-colors touch-target"
                  >
                    <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                    Add Material
                  </button>
                </div>
                {errors.materials && <p className="mt-1 text-sm text-red-500">{errors.materials}</p>}
              </div>

              {/* Instructions */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Instructions <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {activityData.instructions.map((instruction, index) => (
                    <div key={index} className="flex gap-2">
                      <span className="flex-shrink-0 w-8 h-10 flex items-center justify-center text-text-secondary font-semibold">
                        {index + 1}.
                      </span>
                      <textarea
                        value={instruction}
                        onChange={(e) => handleInstructionChange(index, e.target.value, 'activity')}
                        rows={2}
                        className="flex-1 px-4 py-2 border-2 border-border rounded-xl focus:outline-none focus:border-primary resize-none text-base"
                        placeholder="Instruction step..."
                      />
                      {activityData.instructions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveInstruction(index, 'activity')}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors touch-target"
                          aria-label="Remove instruction"
                        >
                          <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => handleAddInstruction('activity')}
                    className="flex items-center gap-2 px-4 py-2 text-primary hover:bg-primary/10 rounded-lg transition-colors touch-target"
                  >
                    <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                    Add Step
                  </button>
                </div>
                {errors.instructions && <p className="mt-1 text-sm text-red-500">{errors.instructions}</p>}
              </div>
            </>
          )}

          {/* Story-specific fields */}
          {contentType === 'story' && (
            <>
              {/* Age Range and Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Age Range <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={storyData.ageRange}
                    onChange={(e) => handleStoryInputChange('ageRange', e.target.value)}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-primary text-base ${
                      errors.ageRange ? 'border-red-500' : 'border-border'
                    }`}
                    required
                    style={{ fontSize: '16px' }}
                  >
                    <option value="">Select age range</option>
                    {AGE_RANGE_OPTIONS.map(age => (
                      <option key={age} value={age}>{age}</option>
                    ))}
                  </select>
                  {errors.ageRange && <p className="mt-1 text-sm text-red-500">{errors.ageRange}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Category
                  </label>
                  <select
                    value={storyData.category}
                    onChange={(e) => handleStoryInputChange('category', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary text-base"
                    style={{ fontSize: '16px' }}
                  >
                    <option value="">Select category (optional)</option>
                    {STORY_CATEGORY_OPTIONS.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Story Content */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Story Content <span className="text-red-500">*</span>
                  <span className="text-xs text-text-secondary font-normal ml-2">
                    ({storyData.content.length}/5000)
                  </span>
                </label>
                <textarea
                  value={storyData.content}
                  onChange={(e) => handleStoryInputChange('content', e.target.value)}
                  rows={10}
                  maxLength={5000}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-primary resize-none text-base ${
                    errors.content ? 'border-red-500' : 'border-border'
                  }`}
                  placeholder="Write your story here..."
                  required
                />
                {errors.content && <p className="mt-1 text-sm text-red-500">{errors.content}</p>}
              </div>

              {/* Read Aloud */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="readAloud"
                  checked={storyData.readAloud}
                  onChange={(e) => handleStoryInputChange('readAloud', e.target.checked)}
                  className="w-5 h-5 text-primary border-2 border-border rounded focus:ring-2 focus:ring-primary"
                />
                <label htmlFor="readAloud" className="text-sm font-semibold text-text-primary cursor-pointer">
                  Good for reading aloud
                </label>
              </div>
            </>
          )}

          {/* Cultural Context - Common for all */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Cultural Context / Story
              <span className="text-xs text-text-secondary font-normal ml-2">
                (Optional, {currentData.culturalContext.length}/1000)
              </span>
            </label>
            <textarea
              value={currentData.culturalContext}
              onChange={(e) => {
                if (contentType === 'recipe') handleRecipeInputChange('culturalContext', e.target.value);
                else if (contentType === 'activity') handleActivityInputChange('culturalContext', e.target.value);
                else handleStoryInputChange('culturalContext', e.target.value);
              }}
              rows={4}
              maxLength={1000}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-primary resize-none text-base ${
                errors.culturalContext ? 'border-red-500' : 'border-border'
              }`}
              placeholder="Share the story or cultural significance..."
            />
            {errors.culturalContext && <p className="mt-1 text-sm text-red-500">{errors.culturalContext}</p>}
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2">
              {TAG_OPTIONS[contentType].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTagToggle(tag)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors touch-target ${
                    currentTags.includes(tag)
                      ? 'bg-primary text-white'
                      : 'bg-surface text-text-secondary hover:bg-gray-200'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Image Selector */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Image (Optional)
            </label>
            <ImageSelector
              onImageSelect={handleImageSelect}
              currentImage={imageData}
              defaultQuery={getDefaultSearchQuery()}
            />
          </div>

          {/* Attribution - Common for all */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Attribution (Optional)
            </label>
            <input
              type="text"
              value={currentData.attribution}
              onChange={(e) => {
                if (contentType === 'recipe') handleRecipeInputChange('attribution', e.target.value);
                else if (contentType === 'activity') handleActivityInputChange('attribution', e.target.value);
                else handleStoryInputChange('attribution', e.target.value);
              }}
              className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary text-base"
              placeholder="e.g., Adapted from Grandma's recipe"
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <MobileButton
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => navigate(-1)}
            >
              Cancel
            </MobileButton>
            <MobileButton
              type="submit"
              variant="primary"
              fullWidth
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : `Submit ${contentType.charAt(0).toUpperCase() + contentType.slice(1)}`}
            </MobileButton>
          </div>
        </form>
      </div>
    </div>
  );
}
